import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './App.css';
import Header from './components/Header';
import LandingPage from './components/LandingPage';
import ReservationForm from './components/ReservationForm';
import ExportarReservas from './components/ExportarReservas';
import GestionReservas from './components/GestionReservas';
import AdminMenuBar from './components/AdminMenuBar';
import AdminCalendario from './components/AdminCalendario';
import AdminLogin from './components/AdminLogin';
import AdminFinanzas from './components/AdminFinanzas';
import AdminDashboard from './components/AdminDashboard';
import GestionHabitaciones from './components/admin/GestionHabitaciones';
import GestionGaleria from './components/admin/GestionGaleria';
import ProtectedRoute from './components/ProtectedRoute';
import { trackPageView } from './utils/analyticsUtils';
import SeasonalTheme from './components/SeasonalTheme';
import GestionPrecios from './components/admin/GestionPrecios';
import RecomendacionesPage from './pages/RecomendacionesPage';
import SubcategoriasPage from './pages/SubcategoriasPage';
import GestionRecomendaciones from './components/admin/GestionRecomendaciones';
import AdminStatistics from './components/AdminStatistics';

// Componente para seguimiento de páginas
function AnalyticsTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // No registrar vistas en el área de administrador
    const isAdminArea = window.location.pathname.startsWith('/admin') || 
                       window.location.hostname.startsWith('admin.');
    
    if (!isAdminArea) {
      // Registrar cambios de página solo en el área pública
      const pageName = location.pathname;
      trackPageView(pageName);
    }
  }, [location]);
  
  return null;
}

// Detectar si estamos en el subdominio de admin
const isAdminSubdomain = window.location.hostname.startsWith('admin.');

// Componente para la aplicación de administrador
function AdminApp() {
  return (
    <div className="App">
      <Helmet>
        <title>Panel de Administración - Hosteria Futaleufu</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <AnalyticsTracker />
      
      <Routes>
        <Route path="/login" element={<AdminLogin />} />
        
        <Route path="/" element={
          <>
            <AdminMenuBar />
            <main className="main-content">
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            </main>
          </>
        } />
        
        <Route path="/exportar" element={
          <>
            <AdminMenuBar />
            <main className="main-content">
              <ProtectedRoute>
                <ExportarReservas />
              </ProtectedRoute>
            </main>
          </>
        } />
        
        <Route path="/calendario" element={
          <>
            <AdminMenuBar />
            <main className="main-content">
              <ProtectedRoute>
                <AdminCalendario />
              </ProtectedRoute>
            </main>
          </>
        } />
        
        <Route path="/habitaciones" element={
          <>
            <AdminMenuBar />
            <main className="main-content">
              <ProtectedRoute>
                <GestionHabitaciones />
              </ProtectedRoute>
            </main>
          </>
        } />
        
        <Route path="/finanzas" element={
          <>
            <AdminMenuBar />
            <main className="main-content">
              <ProtectedRoute>
                <AdminFinanzas />
              </ProtectedRoute>
            </main>
          </>
        } />
        
        <Route path="/precios" element={
          <>
            <AdminMenuBar />
            <main className="main-content">
              <ProtectedRoute>
                <GestionPrecios />
              </ProtectedRoute>
            </main>
          </>
        } />
        
        <Route path="/recomendaciones" element={
          <>
            <AdminMenuBar />
            <main className="main-content">
              <ProtectedRoute>
                <GestionRecomendaciones />
              </ProtectedRoute>
            </main>
          </>
        } />
        
        <Route path="/galeria" element={
          <>
            <AdminMenuBar />
            <main className="main-content">
              <ProtectedRoute>
                <GestionGaleria />
              </ProtectedRoute>
            </main>
          </>
        } />
        
        <Route path="/estadisticas" element={
          <>
            <AdminMenuBar />
            <main className="main-content">
              <ProtectedRoute>
                <AdminStatistics />
              </ProtectedRoute>
            </main>
          </>
        } />
      </Routes>
      
      <footer className="app-footer">
        <p>© {new Date().getFullYear()} Hosteria futaleufú - Todos los derechos reservados</p>
      </footer>
    </div>
  );
}

// Componente para el sitio principal
function MainApp() {
  const AppLayout = ({ children }) => {
    return (
      <div className="App">
        <Header />
        <main className="main-content-reserva">
          <SeasonalTheme showBanner={true} />
          {children}
        </main>
        <footer className="app-footer">
          <p>© {new Date().getFullYear()} Hosteria futaleufú - Todos los derechos reservados</p>
        </footer>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Hosteria Futaleufu</title>
        <meta name="description" content="Hostal con encanto en Futaleufú. Disfruta de nuestras habitaciones cómodas, ubicación privilegiada y ambiente familiar. Reserva tu estancia ahora." />
        <meta name="keywords" content="hostal futaleufu, hosteria futaleufu, reservas hostal, hospedaje, alojamiento, vacaciones, escapada" />
        <meta name="author" content="Hosteria Futaleufu" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta property="og:title" content="Hosteria Futaleufu - Reservas y Hospedaje" />
        <meta property="og:description" content="Descubre nuestro hostal con encanto. Reserva tu estancia en un ambiente familiar y acogedor." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://hosteriafutaleufu.com" />
        <meta property="og:image" content="https://hosteriafutaleufu.com/og-image.jpg" />
        <link rel="canonical" href="https://hosteriafutaleufu.com" />
      </Helmet>
      
      <AnalyticsTracker />
      
      <AppLayout>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/reservar" element={<ReservationForm />} />
          <Route path="/gestionar-reservas" element={<GestionReservas />} />
          <Route path="/gestionar-reservas/:codigoReserva" element={<GestionReservas />} />
          <Route path="/recomendaciones" element={<RecomendacionesPage />} />
          <Route path="/recomendaciones/:categoriaId" element={<SubcategoriasPage />} />
        </Routes>
      </AppLayout>
    </>
  );
}

function App() {
  return (
    <Router>
      {isAdminSubdomain ? <AdminApp /> : <MainApp />}
    </Router>
  );
}

export default App;
